/**
 * Fields needed to pick the SitePage id that page-restriction / FGAC facts
 * target. Folders never yield an origin — they are topology only.
 *
 * Accepts both server (`parent`, `_id`) and Designer (`parentId`, `id`) shapes.
 * `type` is compared case-insensitively (`folder` / `Folder`).
 */
export type PageRestrictionOriginInput = {
  _id?: {toString(): string} | string | null;
  id?: {toString(): string} | string | null;
  type?: 'page' | 'folder' | 'Page' | 'Folder' | string | null;
  parent?: {toString(): string} | string | null;
  parentId?: {toString(): string} | string | null;
  branchId?: {toString(): string} | string | null;
  branchRole?: string | null;
  branchMainPageId?: {toString(): string} | string | null;
};

function pageIdOf(page: PageRestrictionOriginInput): string | undefined {
  const raw = page._id ?? page.id;
  return raw != null ? String(raw) : undefined;
}

function parentIdOf(page: PageRestrictionOriginInput): string | undefined {
  const raw = page.parent ?? page.parentId;
  return raw != null ? String(raw) : undefined;
}

/**
 * Resolves the SitePage identity used for restriction / FGAC checks.
 *
 * - Main page: own id
 * - Site-branch source-backed (`branchMainPageId` set): that main twin id
 * - Site-branch local (`branchRole` set, no `branchMainPageId`): own page id
 * - Legacy page-branch (`branchId`, no `branchRole`): `parent` / `parentId`
 * - Folders / folder ids: never — returns undefined
 *
 * Oso remains the decision point; this helper only picks the SitePage id.
 */
export function resolvePageRestrictionOrigin(
  page: PageRestrictionOriginInput
): string | undefined {
  if (page.type != null && String(page.type).toLowerCase() === 'folder') {
    return undefined;
  }

  const pageId = pageIdOf(page);

  // Source-backed site-branch fork: facts live on the main twin. Checked
  // before branchRole so a malformed row that somehow carries both still
  // anchors on the twin.
  if (page.branchMainPageId != null) {
    return String(page.branchMainPageId);
  }

  // Branch-local site-branch page: authorize against its own SitePage id
  // (restrictions are propagated onto it at create time).
  if (page.branchRole) {
    return pageId;
  }

  // Legacy page-branch: parent names the main page the branch was forked
  // from. Never use this path for site-branch rows (they carry branchRole).
  const parentId = parentIdOf(page);
  if (page.branchId != null && parentId != null) {
    return parentId;
  }

  return pageId;
}

/**
 * Site-branch pages created in the working tree with no main twin. These
 * authorize as their own SitePage and require fail-closed restriction
 * propagation at create time.
 */
export function isSiteBranchLocalPage(page: {
  branchRole?: string | null;
  branchMainPageId?: unknown;
  branchId?: unknown;
}): boolean {
  return Boolean(page.branchRole) && page.branchMainPageId == null;
}
